import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusPill, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, KeyRound, UserMinus, Save, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { formatINR, fmtDate } from "@/utils/format";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [form, setForm] = useState({});
  const [revOpen, setRevOpen] = useState(false);
  const [rev, setRev] = useState({ monthly_amount: "", effective_from: "" });

  const load = () => api.get(`/employees/${id}`).then(({ data }) => { setEmp(data); setForm(data); });
  useEffect(() => {
    load();
    api.get("/designations?include_inactive=false").then(({ data }) => setDesignations(data));
  }, [id]);

  const save = async () => {
    try {
      const payload = {
        full_name: form.full_name, email: form.email, pan: form.pan, dob: form.dob,
        doj: form.doj, dol: form.dol || null, designation_id: form.designation_id,
        department: form.department, bank_name: form.bank_name, ifsc: form.ifsc,
        uan: form.uan, esi_number: form.esi_number, status: form.status,
      };
      if (form.new_bank_account) payload.bank_account = form.new_bank_account;
      await api.put(`/employees/${id}`, payload);
      toast.success("Employee updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addRevision = async () => {
    try {
      await api.post(`/employees/${id}/salary`, { monthly_amount: Number(rev.monthly_amount), effective_from: rev.effective_from });
      toast.success("Salary revision added"); setRevOpen(false); setRev({ monthly_amount: "", effective_from: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async () => {
    try { const { data } = await api.post(`/employees/${id}/reset-password`); toast.success(data.message); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async () => {
    try { await api.delete(`/employees/${id}`); toast.success("Employee deactivated"); navigate("/admin/employees"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!emp) return <Loading />;

  return (
    <div>
      <button onClick={() => navigate("/admin/employees")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-employees">
        <ArrowLeft className="w-4 h-4" /> Employees
      </button>

      <PageHeader title={emp.full_name} description={`${emp.employee_id} · ${emp.designation || "No designation"}`} testid="employee-detail">
        <div className="flex items-center gap-2">
          <StatusPill active={emp.status === "active"} />
          <Button variant="outline" size="sm" className="gap-1.5" onClick={resetPassword} data-testid="reset-password-button"><KeyRound className="w-4 h-4" /> Reset to PAN</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive" data-testid="deactivate-button"><UserMinus className="w-4 h-4" /> Deactivate</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Deactivate {emp.full_name}?</AlertDialogTitle>
                <AlertDialogDescription>They will no longer be included in payroll runs and cannot log in. This is a soft delete — records are retained.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deactivate} data-testid="confirm-deactivate">Deactivate</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-semibold text-lg">Details</h2>
            <Button size="sm" onClick={save} className="gap-1.5" data-testid="save-employee-detail"><Save className="w-4 h-4" /> Save</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Full name"><Input value={form.full_name || ""} onChange={(v) => setForm({ ...form, full_name: v.target.value })} data-testid="detail-name" /></F>
            <F label="Email"><Input value={form.email || ""} onChange={(v) => setForm({ ...form, email: v.target.value })} /></F>
            <F label="PAN"><Input value={form.pan || ""} onChange={(v) => setForm({ ...form, pan: v.target.value.toUpperCase() })} /></F>
            <F label="Designation">
              <Select value={form.designation_id || ""} onValueChange={(v) => setForm({ ...form, designation_id: v })}>
                <SelectTrigger data-testid="detail-designation"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Department"><Input value={form.department || ""} onChange={(v) => setForm({ ...form, department: v.target.value })} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Date of birth"><Input type="date" value={(form.dob || "").slice(0, 10)} onChange={(v) => setForm({ ...form, dob: v.target.value })} /></F>
            <F label="Date of joining"><Input type="date" value={(form.doj || "").slice(0, 10)} onChange={(v) => setForm({ ...form, doj: v.target.value })} /></F>
            <F label="Date of leaving (optional)"><Input type="date" value={(form.dol || "").slice(0, 10)} onChange={(v) => setForm({ ...form, dol: v.target.value })} data-testid="detail-dol" /></F>
            <F label="Bank name"><Input value={form.bank_name || ""} onChange={(v) => setForm({ ...form, bank_name: v.target.value })} /></F>
            <F label={`Account (currently ${emp.bank_account_masked || "—"})`}><Input placeholder="Enter new to change" value={form.new_bank_account || ""} onChange={(v) => setForm({ ...form, new_bank_account: v.target.value })} /></F>
            <F label="IFSC"><Input value={form.ifsc || ""} onChange={(v) => setForm({ ...form, ifsc: v.target.value.toUpperCase() })} /></F>
            <F label="UAN"><Input value={form.uan || ""} onChange={(v) => setForm({ ...form, uan: v.target.value })} /></F>
            <F label="ESI number"><Input value={form.esi_number || ""} onChange={(v) => setForm({ ...form, esi_number: v.target.value })} /></F>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-6 h-fit">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#FF4500]" /> Salary history</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Revisions are effective-dated. Past payslips always use the amount effective that month.</p>
          <Button size="sm" variant="outline" className="w-full gap-1.5 mb-4" onClick={() => setRevOpen(true)} data-testid="add-revision-button"><Plus className="w-4 h-4" /> Add revision</Button>
          <div className="space-y-3">
            {(emp.salary_history || []).map((h, i) => (
              <div key={h.id} className="relative pl-5" data-testid={`salary-history-${i}`}>
                <div className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${i === 0 ? "bg-[#FF4500]" : "bg-border"}`} />
                {i < emp.salary_history.length - 1 && <div className="absolute left-[3px] top-3.5 bottom-[-12px] w-px bg-border" />}
                <div className="font-medium tabular">{formatINR(h.monthly_amount)}</div>
                <div className="text-xs text-muted-foreground">effective {fmtDate(h.effective_from)}{i === 0 && " · current"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent data-testid="revision-dialog">
          <DialogHeader><DialogTitle>Add salary revision</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <F label="New monthly amount (₹)"><Input type="number" value={rev.monthly_amount} onChange={(v) => setRev({ ...rev, monthly_amount: v.target.value })} data-testid="revision-amount" /></F>
            <F label="Effective from"><Input type="date" value={rev.effective_from} onChange={(v) => setRev({ ...rev, effective_from: v.target.value })} data-testid="revision-date" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevOpen(false)}>Cancel</Button>
            <Button onClick={addRevision} disabled={!rev.monthly_amount || !rev.effective_from} data-testid="save-revision-button">Add revision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div><Label className="label-caps">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
